/**
 * 時間の掛かる操作の待ちを「工程」として見せるための composable（#787）。
 *
 * ## なぜ不定スピナーではないのか
 *
 * 応答時間の定石として、**1 秒を超えるならフィードバックを出す、10 秒を超えるなら
 * 進捗の手がかりが要る**とされる。不定スピナーは「受け付けた」ことしか伝えないので、
 * 10 秒級の待ちでは途中から「固まった？」に変わる。クリスタル化はまさにその領域にある
 * （pyxapp のビルド、カバー WebP の変換、OGP カードの合成、Discord 通知の同期送信）。
 *
 * ## なぜクライアント側の推定なのか
 *
 * サーバは工程を返さない（1 リクエストで完結する）。ストリーミングや進捗 API を足すのは
 * 待ちの体感に対して重すぎるので、**クライアントの推定タイマーで進める**。よくある手法で、
 * 大事なのは次の 2 つ:
 *
 * - **実際より遅めに進める。** 先に進みすぎて最後の工程で止まると、かえって長く感じる
 * - **最後の工程で止める。** 勝手に「完了」にしない。完了はサーバの応答が決める
 *
 * 進捗バー（％）にしないのも同じ理由。嘘の数字を出すより、工程名だけのほうが誠実で、
 * 世界観にも乗せやすい。
 */

export interface CeremonyStage {
  /** 表示する文言。 */
  label: string
  /** この工程を表示し続ける時間 (ms)。最後の工程では無視される。 */
  durationMs: number
}

export function useCeremonyStages(stages: CeremonyStage[]) {
  const index = ref(0)
  let timer: ReturnType<typeof setTimeout> | null = null

  const label = computed(() => stages[index.value]?.label ?? '')
  /** 何番目まで進んだか（0 始まり）。演出に使う。 */
  const currentIndex = computed(() => index.value)

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const scheduleNext = () => {
    // 最後の工程では進めない。完了はサーバの応答が決める。
    if (index.value >= stages.length - 1) return
    timer = setTimeout(() => {
      index.value += 1
      scheduleNext()
    }, stages[index.value]!.durationMs)
  }

  const start = () => {
    clear()
    index.value = 0
    scheduleNext()
  }

  const stop = () => {
    clear()
    index.value = 0
  }

  // ダイアログを閉じたまま unmount されてもタイマーを残さない。
  onScopeDispose(clear)

  return { label, currentIndex, start, stop }
}
